import { forwardRef } from "react";
import { Droplets } from "lucide-react";
import { fileUrl } from "../lib/api";

// A4-styled invoice + certificate document for preview + PDF/print export.
export const InvoiceDocument = forwardRef(function InvoiceDocument({ data }, ref) {
  const {
    invoice_number,
    date,
    matter_number,
    purchaser_name,
    vendor_name,
    property_address,
    property_description,
    property_type,
    settlement_date,
    preferred_reading_date,
    basic_fee = 115,
    urgent_fee = 0,
    total,
    results,
    meter_number,
    meter_reading,
    reading_date,
    technician_name,
    photo_path,
    bank_details = { bank: "NAB (National Australia Bank)", bsb: "084 899", account: "273827193", abn: "3040 9071 894" },
  } = data;

  const computedTotal = total != null ? total : basic_fee + urgent_fee;

  return (
    <div
      ref={ref}
      id="invoice-print"
      className="bg-white text-slate-900 mx-auto"
      style={{ width: "100%", maxWidth: 794, fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <div className="flex flex-col p-8 sm:p-10">
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 border-sky-500 pb-5">
          <img src="/logo.png" alt="Flash Property Solutions" className="h-16 w-auto" crossOrigin="anonymous" />
          <div className="text-right">
            <div className="font-display text-lg font-600 text-slate-900 leading-tight">WATER METER READING</div>
            <div className="text-xs tracking-[0.15em] uppercase text-slate-400 font-semibold">
              Invoice &amp; Certificate
            </div>
          </div>
        </div>

        {/* Meta row */}
        <div className="grid grid-cols-3 gap-4 mt-5">
          <MetaCol label="Matter #" value={matter_number} />
          <MetaCol label="Invoice #" value={invoice_number} />
          <MetaCol label="Date" value={date} />
        </div>

        {/* Confirmation details */}
        <div className="mt-6">
          <SectionTitle>Certificate — confirmed details</SectionTitle>
          <div className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100">
            <DetailRow label="Purchaser name" value={purchaser_name} />
            <DetailRow label="Vendor name" value={vendor_name} />
            <DetailRow label="Property address" value={property_address} />
            <div className="grid grid-cols-2 divide-x divide-slate-100">
              <DetailRow label="Property description" value={property_description} />
              <DetailRow label="Property type" value={property_type} />
            </div>
            <div className="grid grid-cols-2 divide-x divide-slate-100">
              <DetailRow label="Settlement date" value={settlement_date} />
              <DetailRow label="Preferred reading date" value={preferred_reading_date} />
            </div>
          </div>
        </div>

        {/* Reading results */}
        <div className="mt-6">
          <SectionTitle>Meter reading</SectionTitle>
          <div className="mt-2 grid grid-cols-3 gap-3">
            <ResultBox label="Meter #" value={meter_number} />
            <ResultBox label="Actual reading" value={meter_reading} />
            <ResultBox label="Reading date" value={reading_date} />
          </div>
          {results ? (
            <div className="mt-3 text-sm text-slate-600">
              <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-slate-400">Results / notes: </span>
              {results}
            </div>
          ) : null}
        </div>

        {/* Photo */}
        <div className="mt-6">
          <SectionTitle>Photo of water meter</SectionTitle>
          <div
            className="mt-2 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center"
            style={{ height: 170 }}
          >
            {photo_path ? (
              <img src={fileUrl(photo_path)} alt="Water meter" crossOrigin="anonymous" className="w-full h-full object-cover" />
            ) : (
              <span className="text-sm text-slate-400">No photo uploaded</span>
            )}
          </div>
        </div>

        {/* Fees */}
        <div className="mt-6">
          <SectionTitle>Fees</SectionTitle>
          <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
            <FeeRow label="Basic Fee" amount={basic_fee} />
            {urgent_fee > 0 && <FeeRow label="Urgent Fee" amount={urgent_fee} />}
            <div className="flex items-center justify-between px-4 py-3 bg-sky-500 text-white">
              <span className="font-display font-600">Total Cost</span>
              <span className="font-display font-700 text-lg">${computedTotal}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-5 grid grid-cols-2 gap-6 border-t border-slate-200">
          <div>
            <SectionTitle>Banking details</SectionTitle>
            <div className="text-sm text-slate-700 mt-1.5 space-y-0.5">
              <div>{bank_details.bank}</div>
              <div>BSB: {bank_details.bsb}</div>
              <div>Account: {bank_details.account}</div>
              <div className="text-xs text-slate-400 mt-1">Reference: property address</div>
            </div>
          </div>
          <div className="text-right">
            <SectionTitle>Technician</SectionTitle>
            <div className="text-sm font-medium text-slate-900 mt-1.5 uppercase">{technician_name || "—"}</div>
            <div className="mt-3">
              <div className="text-[34px] text-slate-300 leading-none" style={{ fontFamily: "'Outfit', cursive" }}>
                {technician_name ? technician_name.split(" ")[0] : ""}
              </div>
              <div className="text-xs text-slate-400 border-t border-slate-300 inline-block pt-1 px-6 mt-1">Signature</div>
            </div>
            <div className="text-xs text-slate-400 mt-3">ABN: {bank_details.abn}</div>
          </div>
        </div>
      </div>
    </div>
  );
});

function SectionTitle({ children }) {
  return <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-sky-600">{children}</div>;
}

function MetaCol({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-slate-400">{label}</div>
      <div className="text-sm font-semibold text-slate-900 mt-0.5">{value || "—"}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="px-4 py-2.5">
      <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-slate-400">{label}</div>
      <div className="text-sm font-medium text-slate-900 mt-0.5">{value || "—"}</div>
    </div>
  );
}

function ResultBox({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-lg px-3 pt-3 pb-4">
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-slate-400">{label}</div>
      <div className="text-sm font-medium text-slate-900 mt-1 leading-relaxed break-words">{value || "—"}</div>
    </div>
  );
}

function FeeRow({ label, amount }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
      <span className="text-sm text-slate-700">{label}</span>
      <span className="text-sm font-medium text-slate-900">${amount}</span>
    </div>
  );
}
